import { Outlet } from 'react-router';
import ApplicationBar from './ApplicationBar';
import AnnouncementCenter from '../Announcements/AnnouncementCenter';
import NavigationBar from './NavigationBar';
import EnvironmentWarning from './EnvironmentWarning';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import Toaster from '../Toast/Toaster';
import ModalProvider from '@components/Modals/ModalContext';
import ModalErrorFallback from '@components/Modals/ModalErrorFallback';
import { ContentContextProvider } from '@/Contexts/ContentContext';
import { store } from '@reduxStore';
import { queueControlledModal, removeControlledModal } from '@components/Modals/modalSlice';
import useModalParams from '@hooks/useModalParams';
import ErrorAnnouncement from '../Announcements/ErrorAnnouncement';

import styles from './Index.module.css';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const modalId = 10001;

/**
 * Effect is watching the url search parameters in regards to the consignment modal.
 * If a form is specified in the url, render the modal/form.
 * We also watch formId to re-render the modal when navigating between different records.
 * Kept in its own component so search param changes re-render only this, not the whole layout.
 */
function ModalSearchParamsSync() {
	const {
		modalParameters: { form, formId }
	} = useModalParams();

	useEffect(() => {
		if (form) {
			// Remove existing modal first to ensure fresh mount when formId changes
			store.dispatch(removeControlledModal(modalId));
			store.dispatch(
				queueControlledModal({
					id: modalId,
					Component: form
				})
			);
		} else {
			store.dispatch(removeControlledModal(modalId));
		}
	}, [form, formId]);

	return null;
}

export default function Index() {
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const appInsights = useAppInsightsContext();

	return (
		<div className={styles['application-container']}>
			<EnvironmentWarning key={'environment-warning'} />

			<AnnouncementCenter key={'announcement-center'} />

			<ApplicationBar
				key={'application-bar'}
				mobileMenuOpen={mobileMenuOpen}
				setMobileMenuOpen={setMobileMenuOpen}
			/>

			<ErrorAnnouncement key={'error-announcement'} />

			<div className={styles['navigation-container']}>
				<NavigationBar
					mobileMenuOpen={mobileMenuOpen}
					setMobileMenuOpen={setMobileMenuOpen}
				/>

				<ContentContextProvider>
					<Outlet />
				</ContentContextProvider>

				<Toaster />
				<ModalSearchParamsSync />
				{/* Modals render outside the route Outlet, so without this boundary a modal error falls through to the full-page error */}
				<ErrorBoundary
					FallbackComponent={ModalErrorFallback}
					onError={(error) => {
						appInsights.trackException({ exception: error instanceof Error ? error : new Error(String(error)) });
					}}
				>
					<ModalProvider />
				</ErrorBoundary>
			</div>
		</div>
	);
}
